import { createRoot } from "react-dom/client"
import {
  type DragEvent,
  type JSX,
  type SyntheticEvent,
  useEffect,
  useRef,
  useState,
} from "react"

type PaperUploadProps = {
  mode: "inline" | "modal"
  action: string
  title: string
  hint: string
  accept: string
  iconUrl: string
  buttonLabel: string
}

function parseMountDataset(el: HTMLElement): PaperUploadProps {
  return {
    mode: el.dataset.paperUploadMode === "modal" ? "modal" : "inline",
    action: el.dataset.paperUploadAction ?? "",
    title: el.dataset.paperUploadTitle ?? "Upload a paper",
    hint: el.dataset.paperUploadHint ?? "PDF, TXT, or Markdown. Drag a file or click here.",
    accept: el.dataset.paperUploadAccept ?? ".pdf,.md,.txt,application/pdf",
    iconUrl: el.dataset.paperUploadIconUrl ?? "",
    buttonLabel: el.dataset.paperUploadButtonLabel ?? "Upload a Paper",
  }
}

type PaperUploadFormProps = Omit<PaperUploadProps, "mode" | "buttonLabel">

function PaperUploadForm({ action, title, hint, accept, iconUrl }: PaperUploadFormProps): JSX.Element {
  const formRef = useRef<HTMLFormElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const xhrRef = useRef<XMLHttpRequest | null>(null)

  const [selectedFileName, setSelectedFileName] = useState("Choose file or drop here")
  const [sourceUrl, setSourceUrl] = useState("")
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [statusText, setStatusText] = useState<string>("")

  // Shared submit path for both a picked file and a URL import. `verb`
  // labels the in-progress state ("Uploading" / "Importing").
  const submitFormData = (formData: FormData, verb: string): void => {
    const form = formRef.current
    if (!form) return

    if (xhrRef.current) {
      xhrRef.current.abort()
      xhrRef.current = null
    }

    const xhr = new XMLHttpRequest()
    xhrRef.current = xhr
    setIsUploading(true)
    setProgress(0)
    setStatusText(`${verb}...`)

    xhr.open((form.method || "POST").toUpperCase(), form.action)
    xhr.responseType = "text"
    xhr.setRequestHeader("Accept", "text/html,application/xhtml+xml")

    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content
    if (csrfToken) {
      xhr.setRequestHeader("X-CSRF-Token", csrfToken)
    }

    xhr.upload.addEventListener("progress", (event) => {
      if (!event.lengthComputable) {
        setStatusText(`${verb}...`)
        return
      }

      const percent = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)))
      setProgress(percent)
      setStatusText(`${verb} ${percent}%`)
    })

    xhr.addEventListener("load", () => {
      xhrRef.current = null
      setIsUploading(false)
      setIsDragging(false)

      if (xhr.status >= 200 && xhr.status < 400) {
        window.location.assign(xhr.responseURL || form.action)
        return
      }

      setStatusText(`${verb} failed (${xhr.status}). Check the file or URL and try again.`)
    })

    xhr.addEventListener("error", () => {
      xhrRef.current = null
      setIsUploading(false)
      setIsDragging(false)
      setStatusText(`${verb} failed.`)
    })

    xhr.addEventListener("abort", () => {
      xhrRef.current = null
      setIsUploading(false)
      setIsDragging(false)
    })

    xhr.send(formData)
  }

  const startUpload = (file: File): void => {
    const input = inputRef.current
    if (!input) return

    const formData = new FormData()
    formData.append(input.name, file)
    submitFormData(formData, "Uploading")
    input.value = ""
  }

  const startUrlImport = (): void => {
    const url = sourceUrl.trim()
    if (!url || isUploading) return

    setSelectedFileName(url)
    const formData = new FormData()
    formData.append("paper[source_url]", url)
    submitFormData(formData, "Importing")
  }

  const handleInputChange = (event: SyntheticEvent<HTMLInputElement>): void => {
    const file = event.currentTarget.files?.[0]
    if (!file) return

    setSelectedFileName(file.name)
    setStatusText("")
    startUpload(file)
  }

  const handleDrop = (event: DragEvent<HTMLLabelElement>): void => {
    event.preventDefault()
    setIsDragging(false)

    const file = event.dataTransfer.files?.[0]
    if (!file) return

    setSelectedFileName(file.name)
    setStatusText("")
    startUpload(file)
  }

  const handleDragOver = (event: DragEvent<HTMLLabelElement>): void => {
    event.preventDefault()
    if (!isUploading) setIsDragging(true)
  }

  const handleDragLeave = (event: DragEvent<HTMLLabelElement>): void => {
    if (event.target === event.currentTarget) {
      setIsDragging(false)
    }
  }

  return (
    <form
      ref={formRef}
      action={action}
      method="post"
      encType="multipart/form-data"
      className={`papers__upload${isDragging ? " is-dragging" : ""}${isUploading ? " is-uploading" : ""}`}
      aria-busy={isUploading}
    >
      <label
        className="papers__dropzone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          name="paper[manuscript]"
          required
          accept={accept}
          className="papers__file-input"
          onChange={handleInputChange}
          disabled={isUploading}
        />

        <div className={`papers__upload-icon${isUploading ? " is-uploading" : ""}`} aria-hidden="true">
          {iconUrl ? <img src={iconUrl} alt="" className="papers__upload-icon-img" /> : null}
        </div>

        <h2 className="papers__upload-title">{title}</h2>
        <p className="papers__upload-hint">{hint}</p>
        <span className="papers__upload-filename">{selectedFileName}</span>

        {!isUploading && statusText ? (
          <span className="papers__upload-progress-text" aria-live="polite">
            {statusText}
          </span>
        ) : null}

        {isUploading ? (
          <div className="papers__upload-progress" aria-live="polite">
            <div className="papers__upload-progress-track">
              <div
                className="papers__upload-progress-bar"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Upload progress"
              />
            </div>
            <span className="papers__upload-progress-text">{statusText}</span>
          </div>
        ) : null}
      </label>

      {/*<div className="papers__url-import">*/}
      {/*  <div className="papers__url-divider" aria-hidden="true">*/}
      {/*    <span>or import from a URL</span>*/}
      {/*  </div>*/}
      {/*  <div className="papers__url-row">*/}
      {/*    <input*/}
      {/*      type="url"*/}
      {/*      className="papers__url-input"*/}
      {/*      placeholder="https://arxiv.org/abs/2401.00001"*/}
      {/*      value={sourceUrl}*/}
      {/*      onChange={(event) => setSourceUrl(event.currentTarget.value)}*/}
      {/*      onKeyDown={(event) => {*/}
      {/*        if (event.key === "Enter") {*/}
      {/*          event.preventDefault()*/}
      {/*          startUrlImport()*/}
      {/*        }*/}
      {/*      }}*/}
      {/*      disabled={isUploading}*/}
      {/*      aria-label="Paper URL"*/}
      {/*      inputMode="url"*/}
      {/*      autoComplete="off"*/}
      {/*    />*/}
      {/*    <button*/}
      {/*      type="button"*/}
      {/*      className="btn btn--primary papers__url-button"*/}
      {/*      onClick={startUrlImport}*/}
      {/*      disabled={isUploading || !sourceUrl.trim()}*/}
      {/*    >*/}
      {/*      Import*/}
      {/*    </button>*/}
      {/*  </div>*/}
      {/*</div>*/}
    </form>
  )
}

function PaperUploadModalApp(props: PaperUploadProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return

    const handleKeydown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false)
      }
    }

    document.addEventListener("keydown", handleKeydown)
    document.body.style.overflow = "hidden"
    requestAnimationFrame(() => closeButtonRef.current?.focus())

    return () => {
      document.removeEventListener("keydown", handleKeydown)
      document.body.style.overflow = ""
    }
  }, [open])

  return (
    <div className="papers-upload-modal">
      <button type="button" className="btn btn--primary papers-upload-modal__trigger" onClick={() => setOpen(true)}>
        {props.buttonLabel}
      </button>

      <div className={`papers-upload-modal__overlay${open ? " is-open" : ""}`} aria-hidden={!open}>
        <div className="papers-upload-modal__backdrop" onClick={() => setOpen(false)} />
        <div className="papers-upload-modal__panel" role="dialog" aria-modal="true" aria-labelledby="paper-upload-modal-title">
          <button
            ref={closeButtonRef}
            type="button"
            className="papers-upload-modal__close"
            aria-label="Close upload dialog"
            onClick={() => setOpen(false)}
          >
            ×
          </button>

          <h2 id="paper-upload-modal-title" className="papers-upload-modal__title">
            {props.title}
          </h2>
          <p className="papers-upload-modal__description">{props.hint}</p>

          <PaperUploadForm {...props} />
        </div>
      </div>
    </div>
  )
}

function PaperUploadApp(props: PaperUploadProps): JSX.Element {
  if (props.mode === "modal") {
    return <PaperUploadModalApp {...props} />
  }

  return <PaperUploadForm {...props} />
}

export function mountPaperUploadApps(): void {
  document.querySelectorAll<HTMLElement>("[data-paper-upload-app]").forEach((el) => {
    const props = parseMountDataset(el)
    createRoot(el).render(<PaperUploadApp {...props} />)
  })
}

